import {useEffect, useState} from "react";
import {IUseValidateResult, ValidationDoc, ValidationResult} from "./types";

//input: the validation doc detailing tests and error response
//output: Array [result object, run validate function, boolean status hasErrors]
export const useValidate = <T,>(doc: ValidationDoc<T>, data: T): IUseValidateResult<T> => {
	const [validationResult, setValidationResult] = useState<ValidationResult<T>>({});
	const [hasErrors, setHasErrors] = useState<boolean>(false);

	const runValidate = (propFilter?: keyof T): boolean => {
		const result: ValidationResult<T> = {};
		const keys = Object.keys(doc);

		keys.filter(prop => !propFilter || (propFilter === prop))
			.forEach((prop) => {
				const propT = prop as keyof T;
				//below should never error. Prop should be keyof T but Object.keys for various reasons returns string[]
				if(!propT) throw new Error("useValidate typing error. Ensure rule props are keyof T");

				for(const test of (doc[propT] ?? [])){
					if(result[propT] !== undefined){
						//Aleady failed a rule for prop, no need to test further
						continue;
					}
					else if(typeof test === "string"){
						//this is a preset rule
						let presetResultErrorMsg: string | undefined = undefined;

						switch(test){
							case "required": {
								presetResultErrorMsg = requiredPresetRule<T>(data, propT);
								break;
							}
							case "email": {
								presetResultErrorMsg = emailPresetRule<T>(data, propT);
								break;
							}
							case "password": {
								presetResultErrorMsg = passwordPresetRule<T>(data, propT);
								break;
							}
							case "postcode uk": {
								presetResultErrorMsg = ukPostcodePresetRule<T>(data, propT)
								break;
							}
						}

						if(presetResultErrorMsg !== undefined){
							result[propT] = presetResultErrorMsg;
						}
					}
					else if(!test.validate(data))
						result[propT] = test.errorMessage;
				}
			});

		setValidationResult(prev => {
			if(propFilter){
				delete prev[propFilter];
			}
			return {...prev, ...result};
		});

		return Object.keys(result).length === 0;
	};

	const resetErrors = (): void => {
		setHasErrors(false);
		setValidationResult({});
	};

	useEffect(() => {
		setHasErrors(Object.keys(validationResult).length > 0);
	}, [validationResult]);

	return [validationResult, runValidate, hasErrors, resetErrors];
};

//No error message returned, empty string will count as invalid causing styling changes without message. 
//An error message felt like overkill for a missing field. Visual stimulas like a red border deemed sufficient
export const requiredPresetRule = <T,>(e: T, prop: keyof T): string | undefined => {
	const isValid = !!e[prop] && (typeof e[prop] !== "string" || (e[prop] as string).length > 0)
	return isValid ? undefined : "";
}

export const emailPresetRule = <T,>(e: T, prop: keyof T): string | undefined => {
	//only test if present
	if(requiredPresetRule(e, prop) !== undefined) return;

	const stringValue = e[prop] as string;
	if(!stringValue)
		throw new Error("EmailPresetRule should only be used on strings!");

	const isValid = e[prop] && !(!/^\S+@\S+\.\S+$/.test(stringValue));
	return isValid ? undefined : "please enter a valid email"
}

export const passwordPresetRule = <T,>(e: T, prop: keyof T): string | undefined => {
	//only test if present
	if(requiredPresetRule(e, prop) !== undefined) return;

	const stringValue = e[prop] as string;
	if(!stringValue)
		throw new Error("PasswordPresetRule should only be used on strings!");

	if (stringValue.length < 8)
		return "password is too short"

	if (!/^(.*\W.*)$/.test(stringValue))
		return "password requires at least 1 symbol"


	if (!/^(.*[a-z].*)$/.test(stringValue))
		return "password requires at least 1 lower case character"

	if (!/^(.*[A-Z].*)$/.test(stringValue))
		return "password requires at least 1 upper case character"

	if (!/^(.*\d.*)$/.test(stringValue))
		return "password requires at least 1 number"

	return undefined;
}

export const ukPostcodePresetRule = <T>(e: T, prop: keyof T): string | undefined => {
	// Only test if present
	if (requiredPresetRule(e, prop) !== undefined) return;

	const postcode = e[prop] as string;
	if (!postcode) {
		return "UK postcode should only be used on strings!";
	}

	// Regular expression pattern for UK postcode validation
	const postcodeRegex = /^(GIR\s?0AA|[A-PR-UWYZ](\d{1,2}|([A-HK-Y]\d|[A-HK-Y]\d(\d|[ABEHMNPRV-Y]))|[0-9][A-HJKS-UW])\s?\d[ABD-HJLNP-UW-Z]{2})$/;

	if (!postcodeRegex.test(postcode)) {
		return "invalid UK postcode";
	}

	return undefined;
};

export default useValidate;
